/**
 * Les actions soumises à droit, `ressource:action`.
 *
 * Elles couvrent les onze entrées de la navigation de la régie (3.2), la
 * console de groupe et la console Artosera. La ressource reprend le nom de la
 * collection (4.1) ; l'action reste en anglais, comme dans l'exemple du
 * document (5.4, `can(account, gallery, "invoices:issue")`).
 */
/** Toutes les actions, dans l'ordre de la navigation. */
export const ACTIONS = [
    'insights:read',
    'insights:write',
    // 1. Tableau de bord
    'dashboard:read',
    // 2. Œuvres
    'artworks:read',
    'artworks:write',
    'artworks:archive',
    'artworks:read-artist-price',
    'artworks:import',
    'artworks:export',
    // Prix modifiés en lot : la direction seule (arbitrage du 21 oct. 2026).
    'artworks:bulk-price',
    'editions:read',
    'editions:write',
    'media:read',
    'media:write',
    'registre-police:read',
    'registre-police:inscrire',
    'registre-police:rectifier',
    // 3. Artistes
    'artists:read',
    'artists:write',
    'treasury:read',
    'treasury:write',
    'treasury:settings',
    'artist-accounts:read',
    'artist-accounts:write',
    'artist-accounts:validate',
    // 4. Expositions
    'exhibitions:read',
    'exhibitions:write',
    'exhibitions:price-list',
    // Agenda et presse (M6).
    'agenda:read',
    'agenda:write',
    'press:read',
    'press:write',
    // 5. Contacts
    'collector-space:read',
    'collector-space:write',
    'contacts:read',
    'contacts:write',
    'contacts:export',
    'contacts:erase',
    'inquiries:read',
    'inquiries:write',
    'profils:read',
    'profils:write',
    'presentation:read',
    'presentation:write',
    'negociation:approuver',
    // 6. Ventes
    'reservations:read',
    'reservations:write',
    'offers:read',
    'offers:write',
    'offers:send',
    'invoices:read',
    'invoices:write',
    'invoices:issue',
    'invoices:credit',
    'invoices:export',
    'online-payments:read',
    'online-payments:write',
    'online-payments:manage',
    'payments:read',
    'payments:write',
    'orders:read',
    'orders:refund',
    'shop:write',
    // Valeurs d'assurance (add-on 15)
    'insurance:read',
    'insurance:write',
    'insurance:settings',
    // 7. Mouvements
    'movements:read',
    'movements:write',
    'consignments:regler',
    'inventaire:read',
    'inventaire:write',
    'inventaire:manage',
    'locations:read',
    'locations:write',
    'locations:facturer',
    // 8. Coffre
    'documents:read',
    'documents:write',
    'documents:read-confidential',
    'documents:archive',
    'document-access:read',
    // Contrats (M5)
    'signatures:read',
    'signatures:write',
    'contracts:read',
    'contracts:write',
    'contracts:templates',
    'artist-contracts:read',
    'artist-contracts:write',
    // 9. Site
    'site:read',
    'site:write',
    'site:preview',
    'site:publish',
    'posts:read',
    'posts:write',
    'pages:read',
    'pages:write',
    'viewing-rooms:read',
    'viewing-rooms:write',
    // 10. Newsletter
    'newsletter:read',
    'newsletter:write',
    'newsletter:send',
    // Diffusion vers les places de marché (M10, module `artsy-artnet`)
    'diffusion:read',
    'diffusion:write',
    'diffusion:manage',
    // 11. Réglages
    // Studio.
    'studio:read',
    'studio:write',
    'ia:read',
    'ia:settings',
    // Assistant de recherche (add-on 24) : les rôles réglés par la direction filtrent en plus.
    'recherche:ask',
    'recherche:settings',
    'settings:read',
    'settings:write',
    // Activer ou couper un module complémentaire (ARTO-193).
    'settings:modules',
    'addons:billing',
    'addons:invoices',
    'settings:team',
    'settings:tax',
    'settings:numbering',
    'settings:templates',
    'settings:locations',
    'settings:export-all',
    'settings:audit',
    // 12. L'équipe
    // La ressource est la COLLECTION, `memberships` (4.1), et non l'écran qui
    // l'affiche. La MODIFIER reste `settings:team` : savoir qui travaille dans
    // la galerie n'est pas régler ses accès (S2-k).
    'memberships:read',
    // Console de groupe (5.2)
    'group:read',
    'group:read-vault-deadlines',
    'group:manage-galleries',
    'group:manage-members',
    // Console Artosera (5.3)
    'console:read',
    'console:provision-gallery',
    // Reprendre un provisionnement interrompu : poser la direction d'une galerie
    // qui n'en a aucune. Plus qu'une création — la galerie existe déjà.
    'console:resume-provisioning',
    // Dossiers d'ouverture (ARTO-127) : lire, tenir ; la pièce contractuelle
    // et le mandat de reprise sont un geste de la direction de Venio.
    'console:read-onboarding',
    'console:manage-onboarding',
    'console:manage-mandate',
    'console:manage-domains',
    'console:manage-backups',
    'console:export-gallery',
    'console:terminate-gallery',
    'console:request-support-access',
    'console:read-audit',
    // Modules complémentaires (D21) : activer et désactiver galerie par
    // galerie, et couper un module pour tout le parc.
    'console:manage-modules',
    'console:manage-addons',
    'console:publish-addon',
    'console:read-addon-sales',
    'console:manage-addon-sales',
    'console:manage-addon-prices',
    'console:manage-addon-billing',
    // Facturation du cœur : prix et souscriptions, tenus par la direction Venio.
    'console:manage-billing',
    'console:kill-switch',
    'console:cut-ia',
    // Espace de test (lot B) : marquer ou démarquer une galerie, ce qui la
    // retire de tous les chiffres de pilotage et de facturation.
    'console:mark-test-gallery',
    // Thème de la régie d'une galerie, et l'autorisation donnée à ses
    // utilisateurs d'en choisir un autre (lot C).
    'console:manage-appearance',
    // Version minimale et conseillée des apps mobiles, par plateforme.
    'console:manage-app-versions',
    // Vue d'aigle (ARTO-58) : une lecture agrégée ouverte aux associés, et les
    // saisies financières de l'espace plateforme.
    'console:read-overview',
    'console:manage-subscriptions',
    'console:manage-receivables',
    'console:manage-tariffs',
    'console:manage-costs',
    'console:manage-milestones',
    'console:manage-associates',
    // Comptabilité de la vue d'aigle : recettes par service et répartition entre
    // les parties prenantes. Lue par la direction et l'associé (sa ligne seule),
    // tenue par la direction — grille de répartition, clôture des mois.
    'console:read-accounting',
    'console:manage-accounting',
    // Reprise des données depuis la console (ARTO-125) : déposer, essayer et
    // lancer un lot sous mandat, et poser ou retirer le mandat contractuel.
    'console:run-import',
    // Défaire un lot de reprise exécuté, ou figer la reprise au scellement
    // (ARTO-132) : supprimer ce qu'un lot a créé, ou ne plus jamais le pouvoir.
    'console:rollback-import',
    // Profils de correspondance (ARTO-130) : écrire un profil ou en publier une
    // version, réutilisable d'une galerie à l'autre.
    'console:manage-import-profiles',
    'creneaux:read',
    'creneaux:write',
    'creneaux:configurer',
    'pipeline:read',
    'pipeline:write',
    'inbox:read',
    'inbox:write',
    'inbox:settings',
    'events:read',
    'events:write',
    'events:checkin',
];
/** La ressource d'une action. */
export function ressourceDe(action) {
    return action.slice(0, action.indexOf(':'));
}
//# sourceMappingURL=actions.js.map